<script setup lang="ts">
    import { gsap } from 'gsap';
    import { ref, onMounted, watch, reactive } from 'vue';
    import { ScrollTrigger, Observer } from 'gsap/all';

    gsap.registerPlugin(ScrollTrigger, Observer);

    const props = defineProps<{
        textList: string[];
        selector: string;
    }>();

    const emits = defineEmits<{
        (e: 'update:modelValue', index: number): void;
    }>();

    const curIndex = ref(0); // 当前高亮项
    const highLightDiff = ref(20); // 高亮起始点距高亮项的距离

    watch(
        curIndex,
        (value) => {
            emits('update:modelValue', value);
        },
        { immediate: true },
    );

    const preTextDeg = ref(-22.5);
    let textItemNodes: Array<HTMLElement> = [];
    let centerLineNode: HTMLElement | null = null;

    const initDomNodes = () => {
        textItemNodes = gsap.utils.toArray('.scroll-text-item');
        centerLineNode = (gsap.utils.toArray('.scroll-text-center-line') as Array<HTMLElement>)[0];
    };

    // 滚动监测，文字高亮变化
    const onScrollUpdate = () => {
        if (!centerLineNode) return;
        const textItemNodesPos = textItemNodes.map((node) => {
            const rect = node.getBoundingClientRect();
            return { top: rect.top, bottom: rect.bottom, height: rect.height };
        });
        const centerLineNodeTop = centerLineNode.getBoundingClientRect().top;
        let tempH = 0;
        for (let index = 0; index < textItemNodesPos.length; index++) {
            const { top, bottom, height } = textItemNodesPos[index];
            const diff = highLightDiff.value >= 0 ? highLightDiff.value : 20;
            if (centerLineNodeTop > top - diff && centerLineNodeTop < bottom + diff) {
                if (height > tempH) {
                    curIndex.value = index;
                    tempH = height;
                }
            }
        }
    };

    const getGsapTimeLine = () => {
        const scrollTextDom: Array<HTMLElement> = gsap.utils.toArray('.scroll-text-wrapper');
        return (
            gsap
                .timeline({
                    scrollTrigger: {
                        // markers: { startColor: 'green', endColor: 'red', fontSize: '12px' },
                        trigger: props.selector,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: true,
                        pin: true,
                        onUpdate: onScrollUpdate,
                    },
                    paused: true,
                    ease: 'none',
                })
                // 滚动第一阶段，展开
                .to(scrollTextDom, {
                    rotationX: -(preTextDeg.value * (props.textList.length - 1)),
                    duration: props.textList.length,
                })
                .fromTo(
                    '.scroll-text-container',
                    {
                        yPercent: -40,
                    },
                    {
                        yPercent: 0,
                        duration: 2,
                    },
                    '<',
                )
            // .to(
            //     '.scroll-text-container',
            //     {
            //         yPercent: 0,
            //         duration: 4,
            //     },
            //     '<',
            // )
        );
    };

    const scrollState = reactive<{
        percentage: number;
        speed: number;
        direction: number;
        animation?: gsap.core.Timeline;
    }>({
        percentage: 0,
        speed: 0.05,
        direction: 1,
    });

    onMounted(() => {
        initDomNodes();
        scrollState.animation = getGsapTimeLine();
    });
</script>

<template>
    <!-- <div class="scroll-text-container" @mouseenter="stopPageScroll" @mouseleave="enablePageScroll"> -->
    <div class="scroll-text-container-height-change-box">
        <div class="scroll-text-container">
            <ul class="scroll-text-wrapper">
                <li
                    v-for="(text, index) in props.textList"
                    :key="index"
                    class="scroll-text-item"
                    :style="`transform: translate(-50%, -50%) rotateX(calc(${
                        index * preTextDeg
                    }deg)) translateZ(15vh);`"
                    :class="['scroll-text-item', curIndex === index ? 'white' : '']"
                >
                    {{ text }}
                </li>
            </ul>
            <span class="scroll-text-center-line" />
        </div>
    </div>
</template>

<style lang="less" scoped>
    .scroll-text-container-height-change-box {
        margin: .convert(60px) [ @vw];
        .scroll-text-container {
            display: flex;
            height: 30vh; // 半径的两倍
            justify-content: center;
            align-items: center;
            width: 100%;
            // background-color: #222;
            overflow: hidden;
            position: relative;
            user-select: none;
            font-family: 'HarmonyOS Sans SC';

            &:hover {
                cursor: all-scroll;
            }
            .scroll-text-wrapper {
                transform-style: preserve-3d;
                transform: perspective(1000px) rotateX(0);
                .scroll-text-item {
                    font-family: 'Montserrat', 'HarmonyOS Sans SC';
                    position: absolute;
                    color: #666;
                    font-size: .convert(40px) [ @vw];
                    white-space: nowrap;
                    font-weight: 400;
                    line-height: 1.172;
                    transform-style: preserve-3d;
                    -webkit-backface-visibility: hidden;
                    backface-visibility: hidden;

                    &.white {
                        color: #fff;
                    }
                }
            }
            .scroll-text-center-line {
                position: absolute;
                width: 100%;
                height: 1px;
                top: 50%;
                left: 0;
                margin-top: -1px;
                background-color: transparent;
            }
        }

        @media (max-width: @screen-mobile-width) {
            margin: .convert(30px) [ @vw-mobile] 0;
            .scroll-text-container {
                .scroll-text-wrapper {
                    transform-style: preserve-3d;
                    transform: perspective(1000px) rotateX(0);
                    .scroll-text-item {
                        position: absolute;
                        color: #666;
                        font-size: .convert(15px) [ @vw-mobile];
                        white-space: nowrap;
                        font-weight: 400;
                        line-height: 1.172;
                        transform-style: preserve-3d;
                        -webkit-backface-visibility: hidden;
                        backface-visibility: hidden;

                        &.white {
                            color: #fff;
                        }
                    }
                }
                .scroll-text-center-line {
                    position: absolute;
                    width: 100%;
                    height: 1px;
                    top: 50%;
                    left: 0;
                    margin-top: -1px;
                    background-color: transparent;
                }
            }
        }
    }
</style>
